import React from 'react';
import { Linking, StyleProp, StyleSheet, TextStyle, View } from 'react-native';

import { CopyButton } from './copy-button';
import { Text } from './text';

/**
 * A single contact value (name, email, phone, address, account #, amount) that
 * is both copyable (#234) and — for email/phone — a tappable mailto:/tel: link
 * (#228). Email values may hold several addresses separated by commas or
 * semicolons; the mailto link lists them all (#228 multi-email support).
 *
 * The copy affordance only renders on web (see CopyButton); on native the value
 * is shown as plain text / a link.
 */
export const ContactValue: React.FC<{
	value: string;
	kind?: 'email' | 'phone' | 'text';
	/** What gets copied; defaults to the displayed value. */
	copyValue?: string;
	style?: StyleProp<TextStyle>;
	numberOfLines?: number;
	/** Set false to hide the copy button (e.g. when the row is already busy). */
	copyable?: boolean;
}> = ({ value, kind = 'text', copyValue, style, numberOfLines, copyable = true }) => {
	const trimmed = (value ?? '').trim();
	if (!trimmed) return null;

	const emails = kind === 'email'
		? trimmed.split(/[;,]/).map((e) => e.trim()).filter(Boolean)
		: [];
	const href = kind === 'email' && emails.length > 0
		? `mailto:${emails.join(',')}`
		: kind === 'phone'
			? `tel:${trimmed.replace(/[^0-9+]/g, '')}`
			: null;

	const open = () => { if (href) Linking.openURL(href).catch(() => { /* ignore */ }); };

	return (
		<View style={styles.row}>
			{href ? (
				<Text onPress={open} style={[styles.link, style]} numberOfLines={numberOfLines}>{trimmed}</Text>
			) : (
				<Text style={style} numberOfLines={numberOfLines}>{trimmed}</Text>
			)}
			{copyable && <CopyButton text={copyValue ?? trimmed} size={14} />}
		</View>
	);
};

const styles = StyleSheet.create({
	row: { flexDirection: 'row', alignItems: 'center', flexShrink: 1 },
	link: { color: '#1976d2', textDecorationLine: 'underline' },
});
